import { AdminModalButton } from "@/admin/AdminModalButton.tsx";
import { Extra, Missing } from "@/admin/AdminTable.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import type { NicIftune } from "@/admin/graphql.gen/graphql.ts";
import { iftuneQueryAtom } from "@/admin/vm/iftuneQuery.ts";
import { averageDescription, burstDescription, peakDescription } from "@/admin/vm/iftuneUnits.tsx";
import { updateNicIftuneMutation } from "@/admin/vm/vmQuery.ts";
import type { GQVmItem } from "@/admin/vm/VmsTable.tsx";
import { formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { VmIftuneBody } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface NicIftuneInputs {
    global: boolean;
    unlimited: boolean;
    average: string;
    peak: string;
    burst: string;
}

function NicIftuneBlock({ value: { average_kbps, burst_kb, peak_kbps } }: { value: NicIftune }) {
    const isNonZero = !!average_kbps || !!peak_kbps || !!burst_kb;

    if (!isNonZero) {
        return <span>Unlimited</span>;
    }
    return (
        <Extra className="whitespace-nowrap text-left" value={<>Average: {average_kbps} KiB/s</>}>
            Peak: {peak_kbps} KiB/s, Burst: {burst_kb} KiB
        </Extra>
    );
}

export function NicIftuneButton({ item }: { item: GQVmItem }) {
    const isDeleted = item.status === "deleted";
    return (
        <AdminModalButton
            isDisabled={isDeleted}
            label={item.nic_iftune ? <NicIftuneBlock value={item.nic_iftune} /> : <Missing />}
        >
            <NicIftuneModalContent item={item} />
        </AdminModalButton>
    );
}

function NicIftuneModalContent({ item }: { item: GQVmItem }) {
    const vmConf = useSuspenseQueryAtom(iftuneQueryAtom);
    const modifyMutation = useStandardMutation(updateNicIftuneMutation);

    const isGlobal = !item.nic_iftune;
    const defaultConf = isGlobal ? vmConf : item.nic_iftune;

    const form = useForm<NicIftuneInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            global: !isGlobal,
            unlimited: item.nic_iftune?.average_kbps === 0,
            average: String(defaultConf?.average_kbps ?? ""),
            peak: String(defaultConf?.peak_kbps ?? ""),
            burst: String(defaultConf?.burst_kb ?? ""),
        },
    });

    const { control, setValue, watch } = form;

    const onSubmit: SubmitHandler<NicIftuneInputs> = async (inputs) => {
        const confBody: VmIftuneBody = !inputs.global
            ? { uuid: item.uuid }
            : inputs.unlimited
              ? { uuid: item.uuid, average_kbps: 0, peak_kbps: 0, burst_kb: 0 }
              : {
                    uuid: item.uuid,
                    average_kbps: inputs.average ? Number(inputs.average) : 0,
                    peak_kbps: inputs.peak ? Number(inputs.peak) : 0,
                    burst_kb: inputs.burst ? Number(inputs.burst) : 0,
                };

        await modifyMutation.mutateAsync({
            body: confBody,
            location: notNull(item._location),
        });
    };

    return (
        <WModalForm
            form={form}
            onSubmit={onSubmit}
            title="Bandwidth limits"
            label="Save"
            buttonProps={{ autoFocus: true }}
        >
            <p className="pb-4">Adjust the bandwidth limits for a specific Virtual Machine.</p>

            <div className="pb-3">
                <Controller
                    control={control}
                    name="global"
                    render={({ field }) => (
                        <WCheckbox
                            isSelected={field.value}
                            onChange={(value) => {
                                if (!value) setValue("unlimited", false);
                                field.onChange(value);
                            }}
                        >
                            Override global limits
                        </WCheckbox>
                    )}
                />
            </div>

            <div className="pb-4">
                <Controller
                    control={control}
                    name="unlimited"
                    render={({ field }) => (
                        <WCheckbox isReadOnly={!watch("global")} isSelected={field.value} onChange={field.onChange}>
                            Unlimited (value 0 means unlimited)
                        </WCheckbox>
                    )}
                />
            </div>

            <Controller
                control={control}
                name="average"
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        isReadOnly={watch("unlimited") || !watch("global")}
                        label="Average (KiB/s)"
                        headless
                        description={averageDescription(props.field.value)}
                    >
                        <AInput type="number" min={0} step={1} />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="peak"
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        isReadOnly={watch("unlimited") || !watch("global")}
                        label="Peak (KiB/s)"
                        headless
                        description={peakDescription(props.field.value)}
                    >
                        <AInput type="number" min={0} step={1} />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="burst"
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        isReadOnly={watch("unlimited") || !watch("global")}
                        label="Burst (KiB)"
                        headless
                        description={burstDescription(props.field.value)}
                    >
                        <AInput type="number" min={0} step={1} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
